import React, { useState, useEffect, useMemo } from 'react';
import { Achievement, AchievementDifficulty, DailyAchievements } from '../types';
import { CloseIcon, PlusIcon } from './Icons';
import { stickerTextStyle } from './StickerText';

interface FootprintAdminModalProps {
    isOpen: boolean;
    onClose: () => void;
    allAchievements: Achievement[];
    dailyAchievements: DailyAchievements;
    onSetDaily: (achievement: Achievement) => void;
    onAddAchievement: (achievement: Omit<Achievement, 'id'>) => void;
    onDeleteAchievement?: (achievementId: number) => void;
    onEditAchievement?: (achievement: Achievement) => void;
    onReorderAchievements?: (achievements: Achievement[]) => void;
}

export const FootprintAdminModal = ({
    isOpen,
    onClose,
    allAchievements,
    dailyAchievements,
    onSetDaily,
    onAddAchievement,
    onDeleteAchievement,
    onEditAchievement,
    onReorderAchievements
}: FootprintAdminModalProps) => {
    const [newAch, setNewAch] = useState({ title: '', description: '', points: 10, difficulty: 'Easy' as AchievementDifficulty, requiredAttachmentType: undefined as 'voice' | 'audio' | 'live_camera' | undefined, isArchived: false });
    const [showAddForm, setShowAddForm] = useState(false);
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [bulkImportJson, setBulkImportJson] = useState('');
    const [editingAch, setEditingAch] = useState<Achievement | null>(null);

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [draggableIndex, setDraggableIndex] = useState<number | null>(null);
    const [localAchievements, setLocalAchievements] = useState<Achievement[]>([]);

    const sortedAchievements = useMemo(() => {
        const dailyId = dailyAchievements?.daily?.id;
        if (!dailyId) return allAchievements;
        const dailyItem = allAchievements.find(a => a.id === dailyId);
        const otherItems = allAchievements.filter(a => a.id !== dailyId);
        return dailyItem ? [dailyItem, ...otherItems] : allAchievements;
    }, [allAchievements, dailyAchievements?.daily?.id]);

    useEffect(() => {
        if (draggedIndex === null) {
            setLocalAchievements(sortedAchievements);
        }
    }, [sortedAchievements, draggedIndex]);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newList = [...localAchievements];
        const draggedItem = newList[draggedIndex];
        newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);

        setDraggedIndex(index);
        setLocalAchievements(newList);
    };

    const handleDragEnd = () => {
        if (draggedIndex !== null) {
            if (onReorderAchievements) {
                onReorderAchievements(localAchievements);
            }
            if (localAchievements.length > 0 && localAchievements[0].id !== dailyAchievements?.daily?.id) {
                onSetDaily(localAchievements[0]);
            }
            setDraggedIndex(null);
        }
        setDraggableIndex(null);
    };

    const handleAdd = (e: React.FormEvent) => {
        e.preventDefault();
        onAddAchievement(newAch);
        setNewAch({ title: '', description: '', points: 10, difficulty: 'Easy', requiredAttachmentType: undefined, isArchived: false });
        setShowAddForm(false);
    };

    const handleSaveEdit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingAch && onEditAchievement) {
            onEditAchievement(editingAch);
            setEditingAch(null);
        }
    };

    const handleBulkImport = (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const parsed = JSON.parse(bulkImportJson);
            if (!Array.isArray(parsed)) throw new Error("JSON must be an array");

            let count = 0;
            parsed.forEach((item: any) => {
                if (item.title && item.description && typeof item.points === 'number') {
                    onAddAchievement({
                        title: item.title,
                        description: item.description,
                        points: item.points,
                        difficulty: item.difficulty || 'Easy',
                        requiredAttachmentType: item.requiredAttachmentType
                    });
                    count++;
                }
            });

            alert(`Successfully imported ${count} achievements.`);
            setBulkImportJson('');
            setShowBulkImport(false);
        } catch (err) {
            alert("Invalid JSON: " + (err instanceof Error ? err.message : String(err)));
            console.error(err);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={onClose}>
            <div className="rounded-2xl shadow-lg p-6 max-w-4xl w-full mx-4 h-[80vh] flex flex-col" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold" style={stickerTextStyle(0.18)}>Footprint Admin Settings</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10"><CloseIcon className="w-5 h-5" /></button>
                </div>

                {editingAch ? (
                    <div className="flex-grow overflow-y-auto min-h-0 -mx-6 px-6 allow-scroll">
                        <form onSubmit={handleSaveEdit} className="space-y-4">
                            <h3 className="font-bold text-lg mb-2" style={stickerTextStyle(0.18)}>Edit Achievement</h3>
                            <input
                                type="text"
                                placeholder="Title"
                                value={editingAch.title}
                                onChange={e => setEditingAch({ ...editingAch, title: e.target.value })}
                                className="w-full bg-gray-800 p-2 rounded text-white"
                                style={stickerTextStyle(0.18)}
                                required
                            />
                            <input
                                type="number"
                                placeholder="Points"
                                value={editingAch.points}
                                onChange={e => setEditingAch({ ...editingAch, points: Number(e.target.value) })}
                                className="w-full bg-gray-800 p-2 rounded text-white"
                                style={stickerTextStyle(0.18)}
                                required
                            />
                            <textarea
                                placeholder="Description"
                                value={editingAch.description}
                                onChange={e => setEditingAch({ ...editingAch, description: e.target.value })}
                                className="w-full bg-gray-800 p-2 rounded text-white h-32"
                                style={stickerTextStyle(0.18)}
                                required
                            />
                            <div className="bg-black/25 p-3 rounded-xl border border-white/5">
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" style={stickerTextStyle(0.14)}>
                                    Required Attachment Type (Optional)
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[
                                        { id: undefined, label: 'None' },
                                        { id: 'voice', label: 'Voice' },
                                        { id: 'audio', label: 'Audio' },
                                        { id: 'live_camera', label: 'Live Cam' }
                                    ].map((opt) => {
                                        const isSelected = editingAch.requiredAttachmentType === opt.id;
                                        return (
                                            <button
                                                key={opt.label}
                                                type="button"
                                                onClick={() => setEditingAch({ ...editingAch, requiredAttachmentType: opt.id as any })}
                                                className={`py-2 px-3 rounded-lg border text-xs font-bold transition-all ${
                                                    isSelected
                                                        ? 'bg-blue-600/35 border-blue-500 text-blue-300'
                                                        : 'bg-gray-900/40 border-white/10 text-gray-400 hover:border-white/20'
                                                }`}
                                                style={stickerTextStyle(0.14)}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="bg-black/25 p-3 rounded-xl border border-white/5 flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" style={stickerTextStyle(0.14)}>Archived</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={editingAch.isArchived || false}
                                        onChange={e => setEditingAch({ ...editingAch, isArchived: e.target.checked })}
                                    />
                                    <div className={`w-11 h-6 rounded-full transition-colors ${editingAch.isArchived ? 'bg-green-500' : 'bg-white/10'}`}></div>
                                    <div className={`absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform ${editingAch.isArchived ? 'translate-x-full' : ''}`}></div>
                                </label>
                            </div>
                            <div className="flex justify-between items-center pt-2">
                                <div className="flex gap-2">
                                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Save Changes</button>
                                    <button type="button" onClick={() => setEditingAch(null)} className="text-gray-400 hover:text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Cancel</button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (onDeleteAchievement && window.confirm(`Delete "${editingAch.title}"?`)) {
                                            onDeleteAchievement(editingAch.id);
                                            setEditingAch(null);
                                        }
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full transition-colors"
                                    style={stickerTextStyle(0.18)}
                                >
                                    Delete
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="flex-grow overflow-y-auto min-h-0 -mx-6 px-6 allow-scroll">
                        <div className="space-y-2">
                            {localAchievements.map((ach, index) => {
                                const isDaily = dailyAchievements?.daily?.id === ach.id;
                                return (
                                    <div
                                        key={ach.id || index}
                                        draggable={draggableIndex === index}
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onClick={() => setEditingAch(ach)}
                                        className={`w-full flex items-center gap-4 p-3 rounded-xl hover:bg-white/10 transition-all cursor-pointer border ${
                                            isDaily 
                                                ? 'bg-blue-600/15 border-blue-500/35 hover:bg-blue-600/25' 
                                                : 'bg-gray-800/40 border-transparent hover:bg-gray-800/60'
                                        } ${draggedIndex === index ? 'opacity-40 scale-[0.98]' : ''}`}
                                    >
                                        <div 
                                            className="text-gray-400 cursor-grab flex items-center justify-center p-1" 
                                            onClick={e => e.stopPropagation()}
                                            onMouseDown={() => setDraggableIndex(index)}
                                            onMouseUp={() => setDraggableIndex(null)}
                                            onTouchStart={() => setDraggableIndex(index)}
                                            onTouchEnd={() => setDraggableIndex(null)}
                                        >
                                            <svg className="w-5 h-5 opacity-40 hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                            </svg>
                                        </div>

                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-white text-base truncate" style={stickerTextStyle(0.18)}>{ach.title}</span>
                                                {isDaily && (
                                                    <span className="bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-500/30" style={stickerTextStyle(0.12)}>
                                                        DAILY TASK
                                                    </span>
                                                )}
                                                {ach.isArchived && (
                                                    <span className="bg-gray-500/20 text-gray-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-500/30" style={stickerTextStyle(0.12)}>
                                                        ARCHIVED
                                                    </span>
                                                )}
                                            </div>
                                            {ach.requiredAttachmentType && (
                                                <span className="bg-red-500/25 border border-red-500/30 text-red-400 text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={stickerTextStyle(0.10)}>
                                                    ⚠️ {ach.requiredAttachmentType.toUpperCase().replace('_', ' ')}
                                                </span>
                                            )}
                                            <p className="text-sm text-gray-400 mt-1 truncate" style={stickerTextStyle(0.18)}>{ach.description}</p>
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <span className="font-bold text-yellow-400 text-base" style={stickerTextStyle(0.18)}>{ach.points} pts</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-700 flex-shrink-0">
                    {showAddForm ? (
                        <form onSubmit={handleAdd}>
                            <h3 className="font-bold text-lg mb-2" style={stickerTextStyle(0.18)}>Add New Achievement</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <input type="text" placeholder="Title" value={newAch.title} onChange={e => setNewAch({ ...newAch, title: e.target.value })} className="bg-gray-800 p-2 rounded text-white" style={stickerTextStyle(0.18)} required />
                                <input type="number" placeholder="Points" value={newAch.points} onChange={e => setNewAch({ ...newAch, points: Number(e.target.value) })} className="bg-gray-800 p-2 rounded text-white" style={stickerTextStyle(0.18)} required />
                                <textarea placeholder="Description" value={newAch.description} onChange={e => setNewAch({ ...newAch, description: e.target.value })} className="bg-gray-800 p-2 rounded col-span-2 text-white h-24" style={stickerTextStyle(0.18)} required />
                                <div className="col-span-2 bg-black/20 p-3 rounded-xl border border-white/5">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" style={stickerTextStyle(0.14)}>
                                        Required Attachment Type (Optional)
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {[
                                            { id: undefined, label: 'None' },
                                            { id: 'voice', label: 'Voice' },
                                            { id: 'audio', label: 'Audio' },
                                            { id: 'live_camera', label: 'Live Cam' }
                                        ].map((opt) => {
                                            const isSelected = newAch.requiredAttachmentType === opt.id;
                                            return (
                                                <button
                                                    key={opt.label}
                                                    type="button"
                                                    onClick={() => setNewAch({ ...newAch, requiredAttachmentType: opt.id as any })}
                                                    className={`py-2 px-3 rounded-lg border text-xs font-bold transition-all ${
                                                        isSelected
                                                            ? 'bg-blue-600/35 border-blue-500 text-blue-300'
                                                            : 'bg-gray-900/40 border-white/10 text-gray-400 hover:border-white/20'
                                                    }`}
                                                    style={stickerTextStyle(0.14)}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="col-span-2 bg-black/25 p-3 rounded-xl border border-white/5 flex items-center justify-between mt-2">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" style={stickerTextStyle(0.14)}>Archived</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer"
                                            checked={newAch.isArchived || false}
                                            onChange={e => setNewAch({ ...newAch, isArchived: e.target.checked })}
                                        />
                                        <div className={`w-11 h-6 rounded-full transition-colors ${newAch.isArchived ? 'bg-green-500' : 'bg-white/10'}`}></div>
                                        <div className={`absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform ${newAch.isArchived ? 'translate-x-full' : ''}`}></div>
                                    </label>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Save</button>
                                    <button type="button" onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Cancel</button>
                                </div>
                            </div>
                        </form>
                    ) : showBulkImport ? (
                        <form onSubmit={handleBulkImport}>
                            <h3 className="font-bold text-lg mb-2" style={stickerTextStyle(0.18)}>Bulk Import (JSON Array)</h3>
                            <textarea placeholder="Paste JSON array here..." value={bulkImportJson} onChange={e => setBulkImportJson(e.target.value)} className="w-full bg-gray-800 p-2 rounded h-32 font-mono text-sm mb-4 text-white" required />
                            <div className="flex gap-2">
                                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Import</button>
                                <button type="button" onClick={() => setShowBulkImport(false)} className="text-gray-400 hover:text-white font-bold py-2 px-4 rounded-full" style={stickerTextStyle(0.18)}>Cancel</button>
                            </div>
                        </form>
                    ) : (
                        <div className="flex gap-4">
                            <button onClick={() => setShowAddForm(true)} className="flex items-center gap-2 bg-green-600/20 hover:bg-green-600/40 text-green-400 font-semibold py-2 px-5 rounded-full transition-colors" style={stickerTextStyle(0.18)}>
                                <PlusIcon className="w-5 h-5" />
                                <span>Add Achievement</span>
                            </button>
                            <button onClick={() => setShowBulkImport(true)} className="flex items-center gap-2 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 font-semibold py-2 px-5 rounded-full transition-colors" style={stickerTextStyle(0.18)}>
                                <span className="text-lg font-bold">{"{ }"}</span>
                                <span>Bulk Import</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
